import { FC, useCallback, useState } from "react";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { FLAT_RATE_PLANS, PREMIUM_FEATURES } from "@common/flatRateBilling";
import { UploadIcon } from "@icons";
import { ConnectPlaid } from "@components/plaid/ConnectPlaid/ConnectPlaid";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { Command } from "@vendor/cmdk/cmdk";
import { useSettings } from "@components/settings/SettingsProvider";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useCanConnectIntegrations } from "@hooks/useCanConnectIntegrations";
import { FeatureUpgradeModal } from "@components/billing/FeatureUpgradeModal";
import { trackEvent } from "@lib/analytics";
import { useOverlays } from "contexts/OverlayContext";
import { OrgConnectProvider } from "contexts/OrgConnectContext";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import styles from "./Commands.module.scss";
import { CommandsDirectory, CommandsListProps } from "./lib";
import { useCommands } from "./useCommands";

interface Props extends CommandsListProps {
    currentDirectory: CommandsDirectory;
}

export const StaticAccountsCommandsList: FC<Props> = ({
    options,
    currentDirectory,
}) => {
    const organizationId = options?.organizationId;

    if (organizationId) {
        return (
            <OrgConnectProvider organizationId={organizationId}>
                <StaticAccountsCommandsListContent
                    options={options}
                    currentDirectory={currentDirectory}
                />
            </OrgConnectProvider>
        );
    }

    return (
        <StaticAccountsCommandsListContent
            options={options}
            currentDirectory={currentDirectory}
        />
    );
};

const StaticAccountsCommandsListContent: FC<Props> = ({
    options,
    currentDirectory,
}) => {
    const isOrgConnect = Boolean(options?.organizationId);
    const { close: closeCommands } = useCommands();
    const { isOpened: isSettingsOpen, closeWithOverlay } = useSettings();
    const { openOverlay } = useOverlays();
    const [showImporterUpgradeModal, setShowImporterUpgradeModal] =
        useState(false);
    const { hasAccess: hasImporterAccess, isFetched: isImporterAccessFetched } =
        useAllowFeature(PREMIUM_FEATURES.IMPORTER);
    const canConnectIntegrations = useCanConnectIntegrations();
    const goToImporter = useCallback(() => {
        closeCommands();
        if (isSettingsOpen) {
            closeWithOverlay("importer", true);
        } else {
            openOverlay("importer", true);
        }
    }, [closeCommands, closeWithOverlay, isSettingsOpen, openOverlay]);
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const handleImportTransactions = useCallback(() => {
        // Refused at the click rather than at the upload: the importer would
        // otherwise open, take a file and parse it before the server said no.
        if (refuseIfViewOnly()) {
            return;
        }

        if (hasImporterAccess) {
            goToImporter();
            return;
        }

        void trackEvent(AnalyticsEvent.UPSELL_IMPORTER_ATTEMPTED);
        setShowImporterUpgradeModal(true);
    }, [goToImporter, hasImporterAccess, refuseIfViewOnly]);

    return (
        <>
            <Command.Group heading="" cmdk-static-group="">
                {canConnectIntegrations && (
                    <ConnectPlaid
                        onConnected={closeCommands}
                        defaultEntity={options?.defaultEntity}
                    >
                        {({ connecting, initializeConnection }) => (
                            <CommandItem
                                onSelect={initializeConnection}
                                disabled={connecting}
                                keywords={["static_score"]}
                            >
                                <InstitutionIcon
                                    institution="plaid-white"
                                    size="sm"
                                />{" "}
                                Add other bank institutions with Plaid
                            </CommandItem>
                        )}
                    </ConnectPlaid>
                )}

                {/* Imported transactions are not a connection, so they cannot be
                listed in the org integrations table. */}
                {currentDirectory === CommandsDirectory.ACCOUNTS &&
                    !isOrgConnect && (
                        <CommandItem
                            onSelect={handleImportTransactions}
                            keywords={["pdf", "images", "csv"]}
                            disabled={!isImporterAccessFetched}
                        >
                            <span className={styles.customIcon}>
                                <UploadIcon />
                            </span>
                            <span>
                                Import Transactions{" "}
                                <span className={styles.info}>
                                    (.csv, .pdf)
                                </span>
                            </span>
                        </CommandItem>
                    )}
            </Command.Group>
            <FeatureUpgradeModal
                show={showImporterUpgradeModal}
                onHide={() => setShowImporterUpgradeModal(false)}
                plan={FLAT_RATE_PLANS.BASIC}
                text={
                    <>
                        Importer is available on{" "}
                        <span className="text-blue-600">Basic</span>
                    </>
                }
                description={
                    <>
                        Upload and automatically transform CSVs and PDFs from
                        any institution into transactions in minutes.
                    </>
                }
            />
        </>
    );
};
